// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { reaction } from 'mobx';
import util from 'preact-util';

/*
 * Partner (AI scribe) journal suggestions — pure helpers plus the store methods
 * journalStore mixes in. Spec: docs/superpowers/specs/2026-10-06-partner-visits-api-design.md §6.
 * The existing per-field AI cards in components/form/input.js render whatever is
 * in holdingStore.aiResponseObject[field] and clear that key when the vet
 * decides; we feed pending vendor sections in and watch the clears.
 */

export function pendingSectionsFromJournal(journal) {
    const list = journal && Array.isArray(journal.externalSuggestions) ? journal.externalSuggestions : [];
    const sorted = [...list].sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));
    const aiResponse = {};
    const index = {};
    const pending = [];
    sorted.forEach((sug) => {
        const pendingSections = (sug.sections || []).filter(sec => sec.status === 'pending');
        if (pendingSections.length === 0) {
            return;
        }
        pendingSections.forEach((sec) => {
            aiResponse[sec.field] = sec.text;
            index[sec.field] = { uuidv4: sug.uuidv4, clientName: sug.clientName, text: sec.text };
        });
        pending.unshift({
            uuidv4: sug.uuidv4,
            clientId: sug.clientId,
            clientName: sug.clientName,
            createdDate: sug.createdDate,
            source: sug.source || null,
            fields: pendingSections.map(sec => sec.field),
        });
    });
    return { aiResponse, index, pending };
}

export function detectDecision(fieldValue, suggestedText) {
    const text = typeof suggestedText === 'string' ? suggestedText.trim() : '';
    const value = typeof fieldValue === 'string' ? fieldValue : '';
    if (!text || !value) {
        return 'dismissed';
    }
    return value.includes(text) ? 'applied' : 'dismissed';
}

export function buildLaunchUrl(launchUrl, params = {}) {
    let url;
    try {
        url = new URL(String(launchUrl || ''));
    } catch (e) {
        return '';
    }
    if (url.protocol !== 'https:') {
        return '';
    }
    ['visitId', 'animalId', 'clinicId', 'userId'].forEach((key) => {
        if (params[key] > 0) {
            url.searchParams.set(key, String(params[key]));
        }
    });
    return url.toString();
}

export function launchersForClinic(items, clinicId) {
    return (Array.isArray(items) ? items : [])
        .filter(item => item && item.approved && item.launchUrl && item.customer === clinicId)
        .map(item => ({
            grantId: item.id,
            clientId: item.clientId,
            clientName: item.clientName,
            logoUrl: item.logoUrl || '',
            launchUrl: item.launchUrl,
        }));
}

// Methods mixed into JournalStore (Object.assign on the prototype). They use
// updateKeyValue so MobX strict mode is respected after awaits.
export const partnerStoreMethods = {
    async loadPartnerLaunchers(clinicId) {
        const response = await util.fetchApi('/api/oauth/grants', { method: 'GET' });
        const items = (response && response.status === 200 && response.data && response.data.items) || [];
        this.updateKeyValue('partnerLaunchers', launchersForClinic(items, clinicId));
    },

    applyPartnerSuggestions(journal) {
        this.disposePartnerWatcher();
        const { aiResponse, index, pending } = pendingSectionsFromJournal(journal);
        this.updateKeyValue('partnerSuggestionIndex', index);
        this.updateKeyValue('partnerPending', pending);
        if (pending.length === 0) {
            return;
        }
        this.updateKeyValue('aiResponseObject', { ...(this.aiResponseObject || {}), ...aiResponse });
        this.watchPartnerCards(journal.id);
    },

    watchPartnerCards(journalId) {
        this.partnerWatcherDisposer = reaction(
            () => Object.keys(this.partnerSuggestionIndex || {})
                .map(field => [field, this.aiResponseObject ? this.aiResponseObject[field] : undefined]),
            (entries) => {
                entries.forEach(([field, cardValue]) => {
                    const entry = this.partnerSuggestionIndex[field];
                    if (!entry || cardValue) {
                        return;
                    }
                    const current = this.newJournal ? this.newJournal[field] : '';
                    const status = detectDecision(current, entry.text);
                    const nextIndex = { ...this.partnerSuggestionIndex };
                    delete nextIndex[field];
                    this.updateKeyValue('partnerSuggestionIndex', nextIndex);
                    this.updateKeyValue('partnerPending', (this.partnerPending || [])
                        .map(p => (p.uuidv4 === entry.uuidv4 ? { ...p, fields: p.fields.filter(f => f !== field) } : p))
                        .filter(p => p.fields.length > 0));
                    this.decidePartnerSuggestion({
                        journalId, uuidv4: entry.uuidv4, field, status,
                    });
                });
            },
        );
    },

    disposePartnerWatcher() {
        if (typeof this.partnerWatcherDisposer === 'function') {
            this.partnerWatcherDisposer();
        }
        this.partnerWatcherDisposer = null;
    },

    clearPartnerSuggestions() {
        this.disposePartnerWatcher();
        const index = this.partnerSuggestionIndex || {};
        const remaining = { ...(this.aiResponseObject || {}) };
        Object.keys(index).forEach((field) => { delete remaining[field]; });
        this.updateKeyValue('aiResponseObject', remaining);
        this.updateKeyValue('partnerSuggestionIndex', {});
        this.updateKeyValue('partnerPending', []);
    },

    async decidePartnerSuggestion({
        journalId, uuidv4, field, status,
    }) {
        if (!(journalId > 0) || !uuidv4 || !field) {
            return null;
        }
        return util.fetchApi(`/api/journals/${journalId}/suggestions/${uuidv4}/decide`, { method: 'POST' }, { field, status });
    },
};
